import type {
  GenerateRequest,
  GenerateResponse,
  RetakeRequest,
  RepaintRequest,
  EditRequest,
  ExtendRequest,
  AudioResponse,
} from '../types';

// Modal endpoint base - each function has its own URL
// Format: https://{workspace}--{app-name}-{function-name}.modal.run
const MODAL_BASE = import.meta.env.VITE_MODAL_BASE || 'https://cdomkam--vybe-sound';

function getEndpointUrl(functionName: string): string {
  return `${MODAL_BASE}-${functionName}.modal.run`;
}

class VybeAPI {
  private authTokenGetter: (() => string | null) | null = null;

  setAuthTokenGetter(getter: () => string | null) {
    this.authTokenGetter = getter;
  }

  private async request<T>(functionName: string, data: unknown): Promise<T> {
    const url = getEndpointUrl(functionName);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add auth header if token available
    const token = this.authTokenGetter?.();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      const error = await response.text();
      throw new Error(error || `Request failed: ${response.status}`);
    }

    return response.json();
  }

  async generate(params: GenerateRequest): Promise<GenerateResponse> {
    return this.request<GenerateResponse>('generate-endpoint', {
      prompt: params.prompt,
      lyrics: params.lyrics || '',
      duration: params.duration || 60,
      inference_steps: params.inference_steps || 60,
      guidance_scale: params.guidance_scale || 15.0,
      seed: params.seed || -1,
      model_id: params.model_id,
      lora_weight: params.lora_weight,
    });
  }

  async retake(params: RetakeRequest): Promise<AudioResponse> {
    return this.request<AudioResponse>('retake-endpoint', {
      source_audio_b64: params.source_audio_b64,
      prompt: params.prompt || '',
      variance: params.variance || 0.5,
      seed: params.seed || -1,
      model_id: params.model_id,
      lora_weight: params.lora_weight,
    });
  }

  async repaint(params: RepaintRequest): Promise<AudioResponse> {
    return this.request<AudioResponse>('repaint-endpoint', {
      source_audio_b64: params.source_audio_b64,
      start_time: params.start_time,
      end_time: params.end_time,
      prompt: params.prompt,
      lyrics: params.lyrics,
    });
  }

  async edit(params: EditRequest): Promise<AudioResponse> {
    return this.request<AudioResponse>('edit-endpoint', {
      source_audio_b64: params.source_audio_b64,
      new_prompt: params.new_prompt,
      new_lyrics: params.new_lyrics,
      edit_n_min: params.edit_n_min || 0.0,
      edit_n_max: params.edit_n_max || 1.0,
    });
  }

  async extend(params: ExtendRequest): Promise<AudioResponse> {
    return this.request<AudioResponse>('extend-endpoint', {
      source_audio_b64: params.source_audio_b64,
      left_seconds: params.left_seconds || 0,
      right_seconds: params.right_seconds || 30,
      prompt: params.prompt || '',
    });
  }

  async health(): Promise<{ status: string; service: string }> {
    const url = getEndpointUrl('health');
    const response = await fetch(url);
    return response.json();
  }
}

export const api = new VybeAPI();
